/**
 * Pure helpers behind the editable draft-items table on the Nutrition page.
 *
 * The table state is a `MealItemDraft[]`; these functions edit rows, total
 * macros, and build the `POST /api/meals` payload. Kept DOM-free so the edit
 * and payload logic is unit-tested directly.
 */
import type { MealItemDraft, SaveMealRequest } from '../api.js';

/** Numeric columns a user can edit on a draft row. */
export type NumericField = 'qty' | 'calories' | 'protein_g' | 'carbs_g' | 'fat_g' | 'caffeine_mg';

export const NUMERIC_FIELDS: readonly NumericField[] = [
  'qty',
  'calories',
  'protein_g',
  'carbs_g',
  'fat_g',
  'caffeine_mg',
];

/** A blank manual row. */
export function emptyItem(): MealItemDraft {
  return {
    name: '',
    qty: null,
    unit: null,
    calories: null,
    protein_g: null,
    carbs_g: null,
    fat_g: null,
    caffeine_mg: null,
    source: 'manual',
  };
}

/** Parse a text input into a finite number or null (blank/invalid → null). */
export function parseNumInput(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Return a new array with row `index` patched (immutable). */
export function updateItem(
  items: MealItemDraft[],
  index: number,
  patch: Partial<MealItemDraft>,
): MealItemDraft[] {
  return items.map((it, i) => (i === index ? { ...it, ...patch } : it));
}

/** Return a new array with row `index` removed. */
export function removeItem(items: MealItemDraft[], index: number): MealItemDraft[] {
  return items.filter((_, i) => i !== index);
}

export interface Totals {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  caffeine_mg: number;
}

/** The macro columns `itemTotals` reads (shared by draft and stored items). */
export type MacroBearing = {
  calories: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  caffeine_mg: number | null;
};

/** Sum macro columns across items, treating null as 0. */
export function itemTotals(items: MacroBearing[]): Totals {
  const acc: Totals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, caffeine_mg: 0 };
  for (const it of items) {
    acc.calories += it.calories ?? 0;
    acc.protein_g += it.protein_g ?? 0;
    acc.carbs_g += it.carbs_g ?? 0;
    acc.fat_g += it.fat_g ?? 0;
    acc.caffeine_mg += it.caffeine_mg ?? 0;
  }
  return acc;
}

/** Rows that carry a non-empty name are savable. */
export function savableItems(items: MealItemDraft[]): MealItemDraft[] {
  return items.filter((it) => it.name.trim().length > 0);
}

/**
 * Build the `POST /api/meals` body from the draft table. Drops nameless rows and
 * trims the meal name; omits `at` (server defaults to now) unless provided.
 */
export function buildMealPayload(opts: {
  name?: string;
  at?: string;
  items: MealItemDraft[];
}): SaveMealRequest {
  const items = savableItems(opts.items).map((it) => ({
    ...it,
    name: it.name.trim(),
    unit: it.unit && it.unit.trim() ? it.unit.trim() : null,
  }));
  const body: SaveMealRequest = { items };
  const name = opts.name?.trim();
  if (name) body.name = name;
  if (opts.at) body.at = opts.at;
  return body;
}
